import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FilterGroupingEnum,
  Task,
  TaskFilterInputObject,
  TaskScopeNameEnum,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import Table from '@lib/components/Table/Table';
import Search from '@lib/components/Search/Search';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import FeatureLockState from '@lib/components/FeatureLockState';
import Trans from '@lib/components/Trans/Trans';
import { IconButtonTypes } from '@lib/components/IconButton/IconButton';
import { PlanState } from '@lib/enums/plan';
import { TableAction } from '@lib/components/Table/types';
import useEdmDocumentationLinkedTasksListQueryHook from 'utils/fetch/hooks/useEdmDocumentationLinkedTasksListQueryHook';
import { APP_URLS } from 'constants/urls';
import useTabListState from '@lib/components/Table/utils/useTabListState';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import getTasksColumns from './utils/getTasksColumns';

const SEARCH_FILTER_SCHEMA = [
  'name',
  'formCategoryName',
  'siteName',
  'siteAreaName',
  'siteCode',
  'siteAreaCode',
  'priority',
];

interface TasksTabProps {
  documentId: string;
}

export default function TasksTab({ documentId }: TasksTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { listState, params, searchQuery, onSearchChange } = useTabListState();
  const { getUserAccessByCompany, handleOpenPlanModal }: ContextProps =
    useOutletContext();
  const isPlanLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const additionalFilters: TaskFilterInputObject[] = useMemo(
    () => [
      {
        documentationsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: documentId },
          filterGroup: 'documentationsId',
        },
      },
    ],
    [documentId],
  );

  const { collection, firstLoading, loading, totalCount } =
    useEdmDocumentationLinkedTasksListQueryHook({
      additionalFilters,
      paginationParams: params,
      orderingParams: params,
      scopeName: TaskScopeNameEnum.Current,
      searchQuery,
      searchQueryFilterSchema: SEARCH_FILTER_SCHEMA,
      skip: isPlanLocked,
    });

  const onViewHandler = useCallback(
    (item: Task) => {
      navigate(
        APP_URLS.dashboard.tasks.view.getDynamicPath({
          pathParts: { id: item.id },
        }),
      );
    },
    [navigate],
  );

  const columns = useMemo(() => getTasksColumns({ t }), [t]);

  const tableActions: TableAction<Task>[] = useMemo(
    () => [
      {
        id: 'view-action',
        icon: 'visibility',
        onClick: onViewHandler,
        type: IconButtonTypes.PrimaryOutlined,
        predictorFnTooltipProps: () => ({ body: t('view') }),
      },
    ],
    [onViewHandler, t],
  );

  if (isPlanLocked) {
    return (
      <FeatureLockState
        title={t('unlock-documentation-in-tasks')}
        text={<Trans i18nKey="unlock-documentation-in-tasks-text" />}
        onUpgrade={() =>
          handleOpenPlanModal(PlanState.Locked, [
            CompanyPlanUpgradeFeatureEnum.Documentation,
          ])
        }
      />
    );
  }

  if (firstLoading) {
    return <CircleLoader />;
  }

  const emptyState = (
    <ListEmptyState
      title={t('no-linked-tasks')}
      text={t('no-linked-tasks-text')}
    />
  );

  return (
    <>
      {((!!totalCount && !!collection.length) || searchQuery) && (
        <Search
          placeholder={t('document-tasks-search-placeholder')}
          value={searchQuery}
          onChange={onSearchChange}
          useSearchParam={false}
        />
      )}
      <Table
        actions={tableActions}
        columns={columns}
        counterText={t('task-plural', { count: totalCount })}
        data={collection}
        emptyState={emptyState}
        id="documentTasks"
        loading={loading}
        orderingState={listState}
        paginationState={listState}
        totalEntries={totalCount}
      />
    </>
  );
}
